import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, CheckCircle2, Clock, HandHelping, Loader2, AlertCircle, XCircle } from 'lucide-react';
import { fetchBenevoleMoi, repondrePlanning } from '../services/api';
import { useAuth } from '../hooks/useAuth';

import { dateHeureFr } from '../utils/dates';
const missionsLabels: Record<string, string> = {
  accueil: 'Accueil',
  logistique: 'Logistique',
  securite: 'Sécurité',
  buvette: 'Buvette',
  animation: 'Animation',
  montage: 'Montage',
  demontage: 'Démontage',
  autre: 'Autre',
};

const statutLabels: Record<string, string> = {
  propose: 'Proposé',
  confirme: 'Confirmé',
  refuse: 'Refusé',
  realise: 'Réalisé',
};

export default function BenevolePage() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const isBenevole = user?.roles?.includes('ROLE_VOLUNTEER') || user?.roles?.includes('ROLE_BUREAU') || user?.roles?.includes('ROLE_ADMIN');

  const query = useQuery({
    queryKey: ['benevole-moi'],
    queryFn: fetchBenevoleMoi,
    enabled: isBenevole,
  });

  const repondreMutation = useMutation({
    mutationFn: ({ id, reponse }: { id: number; reponse: 'confirme' | 'refuse' }) => repondrePlanning(id, reponse),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['benevole-moi'] }),
  });

  if (authLoading || query.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isBenevole) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée aux bénévoles.</p>
          </div>
        </div>
      </div>
    );
  }

  // La fiche du bénévole — tailles, compétences, disponibilités — a rejoint
  // « Mon espace » : on venait ici pour répondre à une affectation, pas pour
  // déclarer sa taille de polo.
  const plannings = query.data?.plannings ?? [];

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <HandHelping className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Mes missions</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Les missions qui me sont proposées.
          </p>
        </div>
      </div>

      {/* Plannings */}
      <div className="card lg:p-6 space-y-4">
        <h2 className="font-semibold text-lg flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Mes missions
        </h2>

        {plannings.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucune mission n'est encore planifiée.
          </p>
        ) : (
          <div className="space-y-3">
            {plannings.map((p: any) => (
              <div key={p.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">{missionsLabels[p.mission] ?? p.mission}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{p.evenement?.nom} — {p.evenement?.lieu}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    p.statutConfirmation === 'confirme' ? 'bg-green-100 text-green-800' :
                    p.statutConfirmation === 'refuse' ? 'bg-red-100 text-red-800' :
                    'bg-amber-100 text-amber-800'
                  }`}>
                    {statutLabels[p.statutConfirmation]}
                  </span>
                </div>

                <p className="text-sm text-gray-600 flex items-center gap-1 mt-2 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  {dateHeureFr(p.debut)} → {dateHeureFr(p.fin)}
                </p>

                {p.statutConfirmation === 'propose' && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => repondreMutation.mutate({ id: p.id, reponse: 'confirme' })}
                      disabled={repondreMutation.isPending}
                      className="inline-flex items-center gap-1 text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Confirmer
                    </button>
                    <button
                      onClick={() => repondreMutation.mutate({ id: p.id, reponse: 'refuse' })}
                      disabled={repondreMutation.isPending}
                      className="inline-flex items-center gap-1 text-sm bg-gray-500 hover:bg-gray-600 text-white px-3 py-1.5 rounded-lg"
                    >
                      <XCircle className="w-4 h-4" /> Refuser
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
